import React, { useEffect, useState } from "react";
import { FaBell } from "react-icons/fa";
import toast from "react-hot-toast";
import { fetchPushStatus, sendTestNotification } from "../../Helper/notificationsApi";
import {
  getBrowserNotificationPermission,
  isAuthenticated,
  isInstalledAppShell,
  subscribeToPushNotifications,
} from "../../Helper/pwa";
import "./NotificationActionCard.css";

/**
 * @param {"enable"|"test"} variant
 */
const NotificationActionCard = ({ variant = "enable" }) => {
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(() => getBrowserNotificationPermission());
  const [deviceList, setDeviceList] = useState([]);

  const loadStatus = async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await fetchPushStatus();
      setDeviceList(data?.deviceList || []);
    } catch (error) {
      console.error("Failed to load push status", error);
    }
  };

  useEffect(() => {
    setPermission(getBrowserNotificationPermission());
    loadStatus();
  }, []);

  if (variant === "enable" && permission === "granted") return null;
  if (variant === "enable" && !isAuthenticated()) return null;

  const isTest = variant === "test";
  const hasAndroid = deviceList.some((d) => /Android/i.test(d.label || ""));
  const thisDeviceReady = permission === "granted";

  const title = isTest ? "Test notifications" : "Enable notifications";
  const description = isTest
    ? hasAndroid
      ? "A test goes to every registered device, including the Android app."
      : "Right now only a computer is registered. Open the CAJobPortal app on your phone, tap Enable on this device, then Allow. After that, send a test."
    : "Receive timely updates on relevant openings, application activity, and hiring decisions.";

  const onEnableThisDevice = async () => {
    setBusy(true);
    const result = await subscribeToPushNotifications();
    setBusy(false);
    setPermission(getBrowserNotificationPermission());
    await loadStatus();
    if (result.success) {
      toast.success(
        isInstalledAppShell()
          ? "This phone is registered. Send a test from your laptop now."
          : "This computer is registered. Enable notifications in the phone app too."
      );
    } else if (result.reason === "permission_denied") {
      toast.error("Allow notifications in Android settings: Apps → CAJobPortal → Notifications");
    } else {
      toast.error("Could not enable notifications on this device.");
    }
  };

  const onSendTest = async () => {
    try {
      setBusy(true);
      const data = await sendTestNotification();
      if (data?.deviceList) setDeviceList(data.deviceList);
      if (data?.deviceList?.some((d) => /Android/i.test(d.label || ""))) {
        toast.success(data.message || "Test sent, including Android.");
      } else if (data?.devices > 0) {
        toast.error(
          data.message ||
            "Sent only to computer. Open the phone app and tap Enable on this device first."
        );
      } else {
        toast("No device subscribed yet.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not send test alert");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notif-action-card">
      <div className="notif-action-card__body">
        <span className="notif-action-card__icon" aria-hidden="true">
          <FaBell />
        </span>
        <div className="notif-action-card__copy">
          <div className="notif-action-card__title">{title}</div>
          <p>{description}</p>
          {isTest && deviceList.length > 0 && (
            <ul className="notif-action-card__devices">
              {deviceList.map((device) => (
                <li key={device.id}>{device.label}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="notif-action-card__actions">
          {(!thisDeviceReady || !isTest) && (
            <button
              type="button"
              className="notif-action-card__btn"
              onClick={onEnableThisDevice}
              disabled={busy || permission === "denied"}
            >
              {busy
                ? "Please wait…"
                : permission === "denied"
                  ? "Notifications blocked"
                  : isTest
                    ? "Enable on this device"
                    : "Enable notifications"}
            </button>
          )}
          {isTest && (
            <button
              type="button"
              className="notif-action-card__btn notif-action-card__btn--secondary"
              onClick={onSendTest}
              disabled={busy}
            >
              {busy ? "Sending…" : "Send test to my devices"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationActionCard;
